import { ISingleInvolvedPartie } from './IClusters';
import { IEnumObject, IReferencial, IUserBrief } from './IGlobal';

export type IHyperlinkDataType = {
    [key: string]:
    | {
        routeName: 'Campaign';
        text: string;
        hyperlinkParameters: {
            campaignId: string;
        }
    }
    | {
        routeName: 'Cluster';
        text: string;
        hyperlinkParameters: {
            clusterId: string;
        }
    }
    | {
        routeName: 'DraftCampaign';
        text: string;
        hyperlinkParameters: {
            campaignId: string;
        }
    }
};

export interface IClusterLinkDataType {
    [key: string]: { name: string, id: string }
}

export interface ISignatureLinkDataType {
    [key: string]: { title: string, clusterId: string }
}

export interface IApprovalLinkDataType {
    [key: string]: { clusterId: string, validationId: string, name: string }
}

export interface IEnumDataType {
    [key: string]: IEnumObject;
}

export interface IPartyDataType {
    [key: string]: ISingleInvolvedPartie[]
}

export interface IPersonDataType {
    [key: string]: IUserBrief
}

export interface IDateDataType {
    [key: string]: string
}

export interface IDateColoredDataType {
    [key: string]: string
}

export interface IDateAgoDataType {
    [key: string]: string
}

export interface IStatusDataType {
    [key: string]: string
}

export interface IExclamationMarkDataType {
    [key: string]: 'True' | 'No'
}

export interface IBooleanDataType {
    [key: string]: boolean
}

export interface INumberDataType {
    [key: string]: { number: string, unit: IReferencial, translatedUnit: string }
}

export enum EColumnType {
    STRING = 'string',
    datetime = 'datetime',
    number = 'number',
    boolean = 'boolean',
    date = 'date',
    dateAgo = 'dateAgo',
    dateColored = 'dateColored',
    text = 'text',
    party = 'party',
    parties = 'parties',
    people = 'people',
    clusterLink = 'clusterLink',
    person = 'person',
    /** Single user or group (principal); same cell shape as party for ColoredDatagrid */
    principal = 'principal',
    /** Multiple principals; same cell shape as parties */
    multiplePrincipals = 'multiplePrincipals',
    legalEntity = 'legalEntity',
    referential = 'referential',
    referentials = 'referentials',
    mail = 'mail',
    status = 'status',
    exclamationMark = 'exclamationMark',
    richText = 'richText',
    richTextFlattened = 'richTextflattened',
    approvalLink = 'approvalLink',
    signatureLink = 'signatureLink',
    enum = 'enum',
    numberWithConditionalColor = 'numberWithConditionalColor',
    hyperlink = 'hyperlink',
    booleanColored = 'booleanColored',
    comment = 'comment',
    pill = 'pill',
    lastEditedBy = 'lastEditedBy',
    overview = 'overview',
    clusterTemplate = 'clusterTemplate',
    extendedAvatar = 'extendedAvatar',
    dropdown = 'dropdown',
    button = 'button',
    radio = 'radio',
    /** Row selection checkbox cell (Design System Checkbox atom) */
    selection = 'selection',
    ACTIVITYMESSAGE = 'activityMessage',
    ATTACHMENTS = 'attachments',
    universalSwitcher = 'universalSwitcher',
    LAST_EDITED_BY_RELATIVE_DATE = 'lastEditedByrelativeDate',
    HYPERLINK_MESSAGES = 'hyperlinkmessages',
    HYPERLINK_DOCUMENTS = 'hyperlinkdocuments',
    HYPERLINK_APPROVAL = 'hyperlinkapproval',
    NUMBER_HASH = 'number#',
    ENUM_STATUS = 'enumstatus',
    STATUS_DATE = 'statusdate',
    STATUS_COLOR = 'statusColor',
    NUMBER_ORDER = 'numberorder',
    OVERVIEW_CLUSTER = 'overviewcluster',
    OVERVIEW_ORGANIZATION = 'overvieworganization',
    PROGRESS = 'progress',
    ICON = 'icon',
    image = 'image'
}

/**
 * Maps column type strings from API metadata (including PascalCase) to EColumnType for grid rendering.
 */
export function normalizeGridColumnType(type: string | EColumnType | undefined): EColumnType | undefined {
    if (type == undefined || type === '') {
        return undefined;
    }
    const lower = String(type).toLowerCase();
    if (lower === 'principal') {
        return EColumnType.principal;
    }
    if (lower === 'multipleprincipals') {
        return EColumnType.multiplePrincipals;
    }
    const entries = Object.entries(EColumnType) as [string, EColumnType][];
    const exact = entries.find(([, v]) => v === type);
    if (exact) {
        return exact[1];
    }
    const ci = entries.find(([, v]) => v.toLowerCase() === lower);
    return ci?.[1];
}

export enum EOverviewColumnSubType {
    organization = 'organization',
    cluster = 'cluster'
}

export enum EColoredDatagridField {
    Status = 'status',
    LastStatusUpdate = 'lastStatusUpdate',
    Cumulated = 'cumulated',
    FinalDecision = 'finalDecision',
    Details = 'details',
    MandateStatus = 'mandateStatus'
}

export enum EColoredDatagridViewParentTypeValue {
    Organization = 'Organization',
    Cluster = 'Cluster',
    Campaign = 'Campaign',
    Platform = 'Platform'
}

export enum EButtonDataType {
    ACTION_BAR = 'actionBar',
    SERVICE = 'service'
}

export enum EColoredDatagridViewTableType {
    FAQ = 'custom-faq',
    QnA = 'custom-rights-cluster',
    AllQnA = 'custom-rights-organization',
    Clausier = 'custom-clauses-organization',
    ClausierPOA = 'custom-clauses-poa-organization',
    ClausierPOADelegation = 'custom-clauses-poa-delegation',
    PoaDelegationChainCluster = 'custom-poa-delegation-chain-cluster',
    SL = 'custom-sideletter-cluster',
    CompositeSL = 'custom-sideletter-organization',
    AllRights = 'custom-all-rights-organization',
    OrganizationSearch = 'custom-search-explore-organization',
    /** Campaign list — separate from Explore saved views (column visibility, etc.). */
    OrganizationCampaign = 'custom-campaign-organization',
    LandlordOwner = 'custom-landlord-owner-organization',
    Approval = 'custom-approval-organization',
    /** Approval Insights dashboard (widgets + table). Separate from Approvals datatable saved views. */
    ApprovalInsights = 'custom-approval-insights-organization',
    Dispute = 'custom-dispute-organization',
    Dispute_Instance = 'custom-dispute-instance-organization',
    OrganizationSettingsTabsMembers = 'custom-settings-tabs-members-organization',
    OrganizationSettingsTabsPendingInvitation = 'custom-settings-tabs-pending-invitation-organization',
    ClusterSettingsTabsMembers = 'custom-settings-members-cluster',
    ClusterSettingsTabsPendingInvitation = 'custom-settings-tabs-pending-invitation-cluster',
    ClusterSettingsTabsAccessRequest = 'custom-settings-tabs-access-request-cluster',
    PlatformMembers = 'custom-members-platform',
    PlatformUsers = 'custom-users-platform',
    PlatformWorkspaces = 'custom-workspaces-platform',
    PlatformPendingInvitations = 'custom-pending-invitations-platform',
    PlatformWorkspaceInvitation = 'custom-workspace-invitation-platform',
    PlatformGroups = 'custom-groups-platform',
    PlatformSingleGroupMembers = 'custom-single-group-members-platform',
    Investors = 'custom-investors-organization',
    KeyDates = 'custom-key-dates-organization',
    CampaignRecipients = 'custom-campaign-recipients-organization',
    ExportControlDetails = 'custom-export-control-details-organization',
    ProfileMandate = 'custom-profile-mandate',
    InquiriesDetails = 'custom-inquiries-details-organization',
    WorkspaceActions = 'custom-workspace-actions-organization',
    ClusterActions = 'custom-cluster-actions-cluster',
    ClusterLinkedClusters = 'custom-linked-clusters-cluster',
    Signature = 'custom-signature-organization',
    OrganizationSettingsCategories = 'custom-settings-categories-organization',
    OrganizationSettingsLimits = 'custom-settings-limits-organization',
    OrganizationSettingsPeriods = 'custom-settings-periods-organization',
    ProfileSubstitution = 'custom-substitution-profile',
    ClusterApprovalOverview = 'custom-approval-overview-cluster',
    ClusterApprovalWorkflowSnapshot = 'custom-approval-workflow-snapshot-cluster',
    ClusterAttachment = 'custom-attachment-cluster',
    ClusterDocumentsStake = 'custom-documents-stake-cluster',
    ClusterKeyDatesOverlay = 'custom-key-dates-cluster',
    GiftWizardDonor = 'custom-gift-donor-organization',
    GiftWizardBeneficiary = 'custom-gift-beneficiary-organization',
    ClusterTemplatesOrganization = 'custom-cluster-templates-organization',
    InquiryProductInformationCluster = 'custom-inquiry-product-information-cluster',
    InquiryWizardCluster = 'custom-inquiry-wizard-cluster',
    InquiryResellersCluster = 'custom-inquiry-resellers-cluster',
    ApprovalDetailsCluster = 'custom-approval-details-cluster',
    /** Organization lobbying actions list (custom-lobbying-actions) */
    LobbyingActionsOrganization = 'custom-lobbying-actions-organization',
    /** Organization lobbying project list (custom-lobbying-project) */
    LobbyingProjectOrganization = 'custom-lobbying-project-organization',
    /** Cluster activity log (activities-datagrid) */
    ActivitiesCluster = 'custom-activities-cluster',
    /** Legal entity screening (TPRM) — parameters shown in cluster panel */
    ScreeningTPRMParameters = 'custom-screening-tprm-parameters-cluster',
    /** Legal entity screening (TPRM) — screening results in cluster panel */
    ScreeningTPRMResults = 'custom-screening-tprm-results-cluster',
    /** Legal entity screening (TPRM) — match details in cluster panel */
    ScreeningTPRMDetails = 'custom-screening-tprm-details-cluster',
    /** Personal screening panel — parameters (isolated from legal-entity LET panel) */
    ScreeningPersonalTPRMParameters = 'custom-screening-personal-tprm-parameters-cluster',
    /** Personal screening panel — results */
    ScreeningPersonalTPRMResults = 'custom-screening-personal-tprm-results-cluster',
    /** Personal screening panel — match details */
    ScreeningPersonalTPRMDetails = 'custom-screening-personal-tprm-details-cluster',
    /** Entity Monitoring panel — MemberCheck monitoring alerts */
    EntityMonitoringAlerts = 'custom-entity-monitoring-alerts-cluster',
    /** TPRM workspace dashboard — legal entity portfolio view */
    TprmEntities = 'custom-tprm-entities-organization',
    /** TPRM workspace dashboard — reviews operational view */
    TprmReviews = 'custom-tprm-reviews-organization',
    /** e-Approval Dashboards — default cross-template "All declaration type" view */
    EApprovalAllDeclarations = 'custom-eapproval-all-declarations-organization',
    /** e-Approval Dashboards — Free declaration template */
    EApprovalFreeDeclaration = 'custom-eapproval-free-declaration-organization',
    /** e-Approval Dashboards — Event Expenses template */
    EApprovalEventExpenses = 'custom-eapproval-event-expenses-organization',
    /** e-Approval Dashboards — Grouping template */
    EApprovalGrouping = 'custom-eapproval-grouping-organization',
    /** e-Approval Dashboards — Sponsoring-Patronage-Communication template */
    EApprovalSponsoring = 'custom-eapproval-sponsoring-organization',
    /** POA holders directory (organization-level) */
    PoaHoldersOrganization = 'custom-poa-holders-organization',
    /** Mandate holders directory (organization-level) */
    MandateHoldersOrganization = 'custom-mandate-holders-organization',
    /** Legal representatives / mandates directory (legal-entity-level) */
    LegalEntityMandate = 'custom-legal-entity-mandate',
    /** COI (conflict of interest) dashboard (organization-level) */
    CoiOrganization = 'custom-coi-organization',
    /** Participations dashboard (organization-level) */
    ParticipationOrganization = 'custom-participation-organization',
    /** Legal entities directory (platform-level) */
    PlatformLegalEntities = 'custom-legal-entities-platform',
    /** Sponsoring dashboard (organization-level) */
    SponsoringOrganization = 'custom-sponsoring-organization',
    /** Trade association dashboard (organization-level) */
    TradeAssociationOrganization = 'custom-trade-association-organization',
    /** Organization settings — Labels tab */
    OrganizationSettingsLabels = 'custom-settings-labels-organization'
}
